import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import GlobalDeleteModal from '../../components/GlobalDeleteModal';
import AddEventForm from './parts/AddEventForm';
import ViewEventModal from './parts/ViewEventModal';

interface EventData {
  id: number;
  title: string;
  body: string;
  event_date: string;
  event_time: string | null;
  event_incharge: string | null;
  posted_by: string;
  is_holiday: boolean; // ← added
  created_at: string;
  updated_at: string;
}

const Ico = ({ d, size = 16, color = 'currentColor', style = {} }: {
  d: string | string[]; size?: number; color?: string; style?: React.CSSProperties;
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke={color} strokeWidth={1.8}
    strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'block', ...style }}>
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const IcoCalendar = (p: { size?: number; color?: string }) => <Ico {...p} d={['M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z']} />;
const IcoPlus     = (p: { size?: number; color?: string }) => <Ico {...p} d={['M12 5v14', 'M5 12h14']} />;
const IcoChevL    = (p: { size?: number; color?: string }) => <Ico {...p} d="M15 18l-6-6 6-6" />;
const IcoChevR    = (p: { size?: number; color?: string }) => <Ico {...p} d="M9 18l6-6-6-6" />;
const IcoEye      = (p: { size?: number; color?: string }) => <Ico {...p} d={['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z','M12 9a3 3 0 100 6 3 3 0 000-6z']} />;
const IcoPencil   = (p: { size?: number; color?: string }) => <Ico {...p} d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />;
const IcoTrash    = (p: { size?: number; color?: string }) => <Ico {...p} d={['M3 6h18','M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6','M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2','M10 11v6','M14 11v6']} />;
const IcoClock    = (p: { size?: number; color?: string }) => <Ico {...p} d={['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z','M12 6v6l4 2']} />;
const IcoX        = (p: { size?: number; color?: string }) => <Ico {...p} d={['M18 6L6 18','M6 6l12 12']} />;
const IcoHome2    = (p: { size?: number; color?: string }) => <Ico {...p} d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" />;
const IcoSpinner  = (p: { size?: number; color?: string }) => <Ico {...p}
  d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"
  style={{ animation: 'ev-spin 1s linear infinite' }} />;

const toYMD = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

const fmtMonthYear = (y: number, m: number) =>
  new Date(y, m, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

const fmtTime = (t: string) =>
  new Date(`1970-01-01T${t}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

const fmtFullDate = (ymd: string) =>
  new Date(ymd + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
  .ev-root * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }

  @keyframes ev-fadeIn   { from{opacity:0}          to{opacity:1} }
  @keyframes ev-slideUp  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes ev-slideIn  { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
  @keyframes ev-shimmer  { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
  @keyframes ev-spin     { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }

  .ev-cell {
    min-height: 72px;
    border-right: 1px solid #f0f4f8;
    border-bottom: 1px solid #f0f4f8;
    padding: 4px 5px 5px;
    cursor: pointer;
    transition: background 0.13s;
    position: relative;
    vertical-align: top;
  }
  .ev-cell:hover { background: rgba(59,130,246,0.04); }
  .ev-cell--other { background: #fafbfc; }
  .ev-cell--other .ev-day-num { color: #c9d2dc; }
  .ev-cell--today { background: rgba(30,58,138,0.03); }
  .ev-cell--selected { background: rgba(59,130,246,0.07) !important; }
  .ev-cell--holiday { background: rgba(251,191,36,0.07) !important; }
  .ev-cell--holiday .ev-day-num:not(.ev-day-num--today) { color: #b45309 !important; }

  .ev-day-num {
    width: 22px; height: 22px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 600; color: #4b5563;
    margin-bottom: 3px; flex-shrink: 0;
    transition: background 0.13s, color 0.13s;
  }
  .ev-day-num--today {
    background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
    color: white !important;
    box-shadow: 0 2px 8px rgba(59,130,246,0.35);
  }
  .ev-day-num--selected:not(.ev-day-num--today) {
    background: rgba(59,130,246,0.15);
    color: #1e3a8a !important;
  }

  .ev-pill {
    display: flex; align-items: center; gap: 4px;
    padding: 1px 5px; border-radius: 4px; margin-bottom: 2px;
    font-size: 10px; font-weight: 600; cursor: pointer;
    background: linear-gradient(90deg, #1e3a8a 0%, #2855ae 100%);
    color: white; white-space: nowrap; overflow: hidden;
    text-overflow: ellipsis; max-width: 100%;
    transition: opacity 0.13s, transform 0.13s;
    animation: ev-slideUp 0.18s ease both;
  }
  .ev-pill:hover { opacity: 0.85; transform: scale(1.02); }
  .ev-pill--holiday {
    background: linear-gradient(90deg, #b45309 0%, #d97706 100%) !important;
  }
  .ev-pill--more {
    background: rgba(30,58,138,0.1); color: #1e3a8a;
    font-size: 10px; font-weight: 700;
  }

  .ev-panel {
    position: fixed; top: 0; right: 0; bottom: 0;
    width: 380px; background: white;
    box-shadow: -4px 0 40px rgba(30,58,138,0.14);
    display: flex; flex-direction: column;
    z-index: 1000;
    animation: ev-slideIn 0.25s cubic-bezier(0.4,0,0.2,1);
  }

  .ev-event-card {
    border-radius: 12px; border: 1px solid rgba(30,58,138,0.1);
    overflow: hidden; background: white;
    transition: box-shadow 0.15s, transform 0.13s;
    animation: ev-slideUp 0.2s ease both;
  }
  .ev-event-card:hover {
    box-shadow: 0 4px 20px rgba(30,58,138,0.12);
    transform: translateY(-1px);
  }
  .ev-event-card--holiday {
    border-color: rgba(180,83,9,0.2) !important;
  }
  .ev-event-card--holiday .ev-card-accent {
    background: linear-gradient(90deg, #b45309, #d97706) !important;
  }

  .ev-act {
    width: 28px; height: 28px; border-radius: 7px;
    display: flex; align-items: center; justify-content: center;
    border: 1px solid transparent; cursor: pointer;
    background: transparent; padding: 0;
    transition: background 0.13s;
  }
  .ev-act:hover      { background: rgba(59,130,246,0.1); border-color: rgba(59,130,246,0.2); }
  .ev-act--red:hover { background: rgba(239,68,68,0.1);  border-color: rgba(239,68,68,0.2); }

  .ev-nav {
    width: 34px; height: 34px; border-radius: 10px;
    background: rgba(30,58,138,0.07); border: 1px solid rgba(30,58,138,0.13);
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    transition: background 0.13s; padding: 0;
  }
  .ev-nav:hover { background: rgba(30,58,138,0.14); }

  .ev-shimmer {
    background: linear-gradient(90deg,#f0f4f8 25%,#e2e8f0 50%,#f0f4f8 75%);
    background-size: 600px 100%;
    animation: ev-shimmer 1.3s infinite linear;
    border-radius: 6px;
  }

  .ev-scroll::-webkit-scrollbar { width: 4px; }
  .ev-scroll::-webkit-scrollbar-track { background: transparent; }
  .ev-scroll::-webkit-scrollbar-thumb { background: rgba(30,58,138,0.15); border-radius: 4px; }

  .ev-add-btn {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 20px; border-radius: 12px; border: none;
    background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
    color: white; cursor: pointer; font-size: 14px; font-weight: 600;
    box-shadow: 0 4px 12px rgba(30,58,138,0.3);
    font-family: 'DM Sans', sans-serif;
    transition: box-shadow 0.18s, transform 0.14s;
  }
  .ev-add-btn:hover {
    box-shadow: 0 6px 20px rgba(30,58,138,0.4);
    transform: translateY(-1px);
  }

  .ev-today-btn {
    display: flex; align-items: center; gap: 7px;
    padding: 7px 14px; border-radius: 9px;
    background: rgba(30,58,138,0.07); border: 1px solid rgba(30,58,138,0.15);
    color: #1e3a8a; font-weight: 600; font-size: 13px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; transition: background 0.13s;
  }
  .ev-today-btn:hover { background: rgba(30,58,138,0.13); }
`;

const EventCard: React.FC<{
  event: EventData; index: number;
  onView: () => void; onEdit: () => void; onDelete: () => void;
}> = ({ event, index, onView, onEdit, onDelete }) => (
  <div className={`ev-event-card${event.is_holiday ? ' ev-event-card--holiday' : ''}`} style={{ animationDelay: `${index * 0.05}s` }}>
    <div className="ev-card-accent" style={{ height: 3, background: 'linear-gradient(90deg,#1e3a8a,#3b82f6)' }} />
    <div style={{ padding: '11px 13px' }}>

      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:6, marginBottom:7 }}>
        <div style={{ display:'flex', alignItems:'center', gap:5, flexWrap:'wrap' }}>
          {event.event_time ? (
            <span style={{
              display:'flex', alignItems:'center', gap:4,
              fontSize:11, fontWeight:700, color:'#3b82f6',
              background:'rgba(59,130,246,0.09)', border:'1px solid rgba(59,130,246,0.18)',
              padding:'2px 7px', borderRadius:5, fontFamily:'monospace',
            }}>
              <IcoClock size={10} color="#3b82f6" />{fmtTime(event.event_time)}
            </span>
          ) : (
            <span style={{
              fontSize:11, fontWeight:600, color:'#9ca3af',
              background:'#f9fafb', border:'1px solid #e5e7eb',
              padding:'2px 7px', borderRadius:5,
            }}>All day</span>
          )}
          {event.is_holiday && (
            <span style={{
              fontSize:10, fontWeight:700,
              color:'#b45309', background:'rgba(251,191,36,0.15)',
              border:'1px solid rgba(251,191,36,0.4)',
              padding:'2px 7px', borderRadius:5,
            }}>🏖 Holiday</span>
          )}
        </div>
        <div style={{ display:'flex', gap:3 }} onClick={e => e.stopPropagation()}>
          <button className="ev-act"             title="View"   onClick={onView}  ><IcoEye    size={13} color="#60a5fa" /></button>
          <button className="ev-act"             title="Edit"   onClick={onEdit}  ><IcoPencil size={13} color="#3b82f6" /></button>
          <button className="ev-act ev-act--red" title="Delete" onClick={onDelete}><IcoTrash  size={13} color="#ef4444" /></button>
        </div>
      </div>

      <div style={{ fontSize:13, fontWeight:700, color:'#111827', marginBottom:4, lineHeight:1.3 }}>
        {event.title}
      </div>
      <div style={{
        fontSize:11, color:'#6b7280', lineHeight:1.5, marginBottom:8,
        display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden',
      }}>
        {event.body}
      </div>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:5 }}>
        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
          <div style={{
            width:20, height:20, borderRadius:'50%',
            background:'linear-gradient(135deg,#1e3a8a,#3b82f6)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:8, fontWeight:800, color:'white',
          }}>{event.posted_by.charAt(0).toUpperCase()}</div>
          <span style={{ fontSize:11, color:'#9ca3af' }}>{event.posted_by}</span>
        </div>
        {event.event_incharge && (
          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
            <div style={{
              width:18, height:18, borderRadius:'50%',
              background:'linear-gradient(135deg,#0f766e,#14b8a6)',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:7, fontWeight:800, color:'white',
            }}>{event.event_incharge.charAt(0).toUpperCase()}</div>
            <span style={{ fontSize:11, color:'#6b7280' }}>{event.event_incharge}</span>
          </div>
        )}
      </div>
    </div>
  </div>
);

const DayPanel: React.FC<{
  date: string;
  events: EventData[];
  loading: boolean;
  onClose: () => void;
  onAdd: () => void;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (ev: EventData) => void;
}> = ({ date, events, loading, onClose, onAdd, onView, onEdit, onDelete }) => (
  <div className="ev-panel">
    <div style={{
      padding:'18px 20px', flexShrink:0,
      background:'linear-gradient(135deg,#1e3a8a 0%,#2855ae 55%,#3b82f6 100%)',
    }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
        <div>
          <div style={{ fontSize:10, fontWeight:700, color:'rgba(255,255,255,0.55)', textTransform:'uppercase', letterSpacing:'0.7px', marginBottom:3 }}>
            Selected Day
          </div>
          <div style={{ fontSize:15, fontWeight:800, color:'white', lineHeight:1.2 }}>
            {fmtFullDate(date)}
          </div>
        </div>
        <button onClick={onClose} style={{
          width:32, height:32, borderRadius:9, background:'rgba(255,255,255,0.15)',
          border:'1px solid rgba(255,255,255,0.25)', cursor:'pointer',
          display:'flex', alignItems:'center', justifyContent:'center', padding:0,
        }}>
          <IcoX size={15} color="white" />
        </button>
      </div>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{
          minWidth:36, height:32, borderRadius:9, padding:'0 12px',
          background:'rgba(255,255,255,0.18)', border:'1px solid rgba(255,255,255,0.28)',
          display:'flex', alignItems:'center', gap:6,
          fontSize:13, fontWeight:800, color:'white',
        }}>
          {loading ? <IcoSpinner size={13} color="rgba(255,255,255,0.8)" /> : (
            <><span style={{ fontSize:16 }}>{events.length}</span>
            <span style={{ fontSize:10, fontWeight:600, color:'rgba(255,255,255,0.65)' }}>
              event{events.length !== 1 ? 's' : ''}
            </span></>
          )}
        </div>
        <button onClick={onAdd} style={{
          display:'flex', alignItems:'center', gap:6, padding:'7px 14px',
          border:'1px solid rgba(255,255,255,0.28)', borderRadius:9,
          background:'rgba(255,255,255,0.15)', color:'white', cursor:'pointer',
          fontSize:12, fontWeight:700, fontFamily:'DM Sans,sans-serif',
        }}>
          <IcoPlus size={12} color="white" />Add Event
        </button>
      </div>
    </div>

    <div className="ev-scroll" style={{ flex:1, overflowY:'auto', padding:16, display:'flex', flexDirection:'column', gap:10 }}>
      {loading ? (
        Array.from({length:2}).map((_,i) => (
          <div key={i} style={{ borderRadius:12, overflow:'hidden', border:'1px solid #f0f4f8' }}>
            <div className="ev-shimmer" style={{ height:3 }} />
            <div style={{ padding:12, display:'flex', flexDirection:'column', gap:8 }}>
              <div className="ev-shimmer" style={{ height:11, width:'25%' }} />
              <div className="ev-shimmer" style={{ height:14, width:'65%' }} />
              <div className="ev-shimmer" style={{ height:10, width:'85%' }} />
            </div>
          </div>
        ))
      ) : events.length === 0 ? (
        <div style={{
          display:'flex', flexDirection:'column', alignItems:'center',
          justifyContent:'center', padding:'40px 20px', gap:12,
          animation:'ev-fadeIn 0.3s ease',
        }}>
          <div style={{
            width:54, height:54, borderRadius:14,
            background:'rgba(30,58,138,0.06)', border:'1.5px dashed rgba(59,130,246,0.25)',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
            <IcoCalendar size={24} color="#93c5fd" />
          </div>
          <div style={{ fontSize:14, fontWeight:700, color:'#374151' }}>No events</div>
          <div style={{ fontSize:12, color:'#9ca3af', textAlign:'center', lineHeight:1.6 }}>
            Nothing scheduled.<br />Click <strong>Add Event</strong> to create one.
          </div>
        </div>
      ) : events.map((ev, i) => (
        <EventCard
          key={ev.id} event={ev} index={i}
          onView={() => onView(ev.id)}
          onEdit={() => onEdit(ev.id)}
          onDelete={() => onDelete(ev)}
        />
      ))}
    </div>
  </div>
);

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CalendarGrid: React.FC<{
  year: number; month: number;
  selectedDate: string;
  monthEvents: Record<string, EventData[]>;
  onSelectDate: (ymd: string) => void;
  onPillClick: (ev: EventData) => void;
}> = ({ year, month, selectedDate, monthEvents, onSelectDate, onPillClick }) => {
  const today    = toYMD(new Date());
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMo = new Date(year, month + 1, 0).getDate();
  const daysInPr = new Date(year, month, 0).getDate();

  const cells: { ymd: string; day: number; cur: boolean }[] = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPr - i;
    cells.push({ ymd:`${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`, day:d, cur:false });
  }
  for (let d = 1; d <= daysInMo; d++) {
    cells.push({ ymd:`${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`, day:d, cur:true });
  }
  while (cells.length < 42) {
    const d  = cells.length - firstDay - daysInMo + 1;
    const nm = month + 2 > 12 ? 1      : month + 2;
    const ny = month + 2 > 12 ? year+1 : year;
    cells.push({ ymd:`${ny}-${String(nm).padStart(2,'0')}-${String(d).padStart(2,'0')}`, day:d, cur:false });
  }

  const MAX_PILLS = 3;

  return (
    <div style={{ width:'100%' }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', borderBottom:'2px solid #e5e7eb' }}>
        {WEEK_DAYS.map((d, i) => (
          <div key={d} style={{
            padding:'7px 0', textAlign:'center',
            fontSize:11, fontWeight:800, color: i === 0 || i === 6 ? '#9ca3af' : '#6b7280',
            textTransform:'uppercase', letterSpacing:'0.5px',
            borderRight: i < 6 ? '1px solid #f0f4f8' : 'none',
          }}>
            {d}
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)' }}>
        {cells.map((cell, i) => {
          const isToday    = cell.ymd === today;
          const isSel      = cell.ymd === selectedDate;
          const evs        = monthEvents[cell.ymd] ?? [];
          const hasHoliday = evs.some(e => e.is_holiday); // ← is_holiday
          const shown      = evs.slice(0, MAX_PILLS);
          const overflow   = evs.length - MAX_PILLS;

          return (
            <div
              key={i}
              className={[
                'ev-cell',
                !cell.cur    ? 'ev-cell--other'    : '',
                isToday      ? 'ev-cell--today'    : '',
                isSel        ? 'ev-cell--selected' : '',
                hasHoliday   ? 'ev-cell--holiday'  : '', // ← is_holiday
              ].join(' ')}
              onClick={() => onSelectDate(cell.ymd)}
              style={{ borderLeft: i % 7 === 0 ? 'none' : undefined }}
            >
              <div className={[
                'ev-day-num',
                isToday ? 'ev-day-num--today'    : '',
                isSel   ? 'ev-day-num--selected' : '',
              ].join(' ')}>
                {cell.day}
              </div>

              {shown.map((ev, pi) => (
                <div
                  key={ev.id}
                  className={`ev-pill${ev.is_holiday ? ' ev-pill--holiday' : ''}`} // ← is_holiday
                  style={{ animationDelay:`${pi * 0.04}s` }}
                  onClick={e => { e.stopPropagation(); onPillClick(ev); }}
                  title={ev.title}
                >
                  {ev.event_time && (
                    <span style={{ opacity:0.75, fontSize:9, fontFamily:'monospace', flexShrink:0 }}>
                      {fmtTime(ev.event_time)}
                    </span>
                  )}
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis' }}>{ev.title}</span>
                </div>
              ))}

              {overflow > 0 && (
                <div className="ev-pill ev-pill--more">+{overflow} more</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Events: React.FC = () => {
  const today = new Date();
  const [calYear,      setCalYear]      = useState(today.getFullYear());
  const [calMonth,     setCalMonth]     = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const [monthEvents,  setMonthEvents]  = useState<Record<string, EventData[]>>({});
  const [monthLoading, setMonthLoading] = useState(false);
  const [dayEvents,    setDayEvents]    = useState<EventData[]>([]);
  const [dayLoading,   setDayLoading]   = useState(false);

  const [viewOpen,      setViewOpen]      = useState(false);
  const [selectedId,    setSelectedId]    = useState<number | null>(null);
  const [addEditOpen,   setAddEditOpen]   = useState(false);
  const [editingId,     setEditingId]     = useState<number | null>(null);
  const [deleteOpen,    setDeleteOpen]    = useState(false);
  const [deletingEv,    setDeletingEv]    = useState<EventData | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const toast = useRef<Toast>(null);

  const fetchMonth = useCallback(async (y: number, m: number) => {
    setMonthLoading(true);
    const ym = `${y}-${String(m+1).padStart(2,'0')}`;
    try {
      const res = await apiClient.get(`/events-by-month?month=${ym}`);
      if (res.data.status === 'success') {
        const grouped: Record<string, EventData[]> = {};
        (res.data.events as EventData[]).forEach(ev => {
          const k = ev.event_date.substring(0, 10);
          if (!grouped[k]) grouped[k] = [];
          grouped[k].push(ev);
        });
        Object.values(grouped).forEach(arr =>
          arr.sort((a, b) => (a.event_time ?? '').localeCompare(b.event_time ?? ''))
        );
        setMonthEvents(grouped);
      }
    } catch { /* silent */ }
    finally { setMonthLoading(false); }
  }, []);

  const fetchDay = useCallback(async (ymd: string) => {
    setDayLoading(true);
    setDayEvents([]);
    try {
      const res = await apiClient.get(`/events-by-date?event_date=${ymd}&limit=100`);
      if (res.data.status === 'success') setDayEvents(res.data.events ?? []);
    } catch {
      toast.current?.show({ severity:'error', summary:'Error', detail:'Failed to load events', life:4000 });
    } finally { setDayLoading(false); }
  }, []);

  useEffect(() => { fetchMonth(calYear, calMonth); }, [calYear, calMonth]);
  useEffect(() => { if (selectedDate) fetchDay(selectedDate); }, [selectedDate]);

  const refreshBoth = () => {
    fetchMonth(calYear, calMonth);
    if (selectedDate) fetchDay(selectedDate);
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalYear(y => y-1); setCalMonth(11); }
    else setCalMonth(m => m-1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalYear(y => y+1); setCalMonth(0); }
    else setCalMonth(m => m+1);
  };

  const handleSelectDate = (ymd: string) => setSelectedDate(prev => prev === ymd ? null : ymd);
  const handlePillClick  = (ev: EventData) => setSelectedDate(ev.event_date.substring(0, 10));

  const handleDeleteConfirm = async () => {
    if (!deletingEv) return;
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/events/${deletingEv.id}`);
      toast.current?.show({ severity:'success', summary:'Deleted', detail:'Event removed.', life:4000 });
      setDeleteOpen(false); setDeletingEv(null);
      refreshBoth();
    } catch (err: any) {
      toast.current?.show({ severity:'error', summary:'Error', detail:err.response?.data?.message || 'Delete failed', life:5000 });
    } finally { setDeleteLoading(false); }
  };

  const totalMonth = Object.values(monthEvents).reduce((a, b) => a + b.length, 0);
  const panelOpen  = selectedDate !== null;

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <style>{CSS}</style>

      <div className="ev-root" style={{ minHeight:'100vh' }}>
        <div style={{
          background:'white', borderRadius:'20px', padding:'20px 24px',
          marginBottom:'20px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)',
          border:'1px solid #e5e7eb', display:'flex',
          justifyContent:'space-between', alignItems:'center',
          flexWrap:'wrap', gap:16,
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <div style={{
              width:50, height:50,
              background:'linear-gradient(135deg,#1e3a8a 0%,#3b82f6 100%)',
              borderRadius:15, display:'flex', alignItems:'center', justifyContent:'center',
              boxShadow:'0 4px 12px rgba(30,58,138,0.25)', flexShrink:0,
            }}>
              <IcoCalendar size={23} color="white" />
            </div>
            <div>
              <h1 style={{ margin:0, color:'#1f2937', fontSize:22, fontWeight:700, letterSpacing:'-0.5px' }}>
                Events
              </h1>
              <div style={{ color:'#6b7280', fontSize:13, marginTop:3, fontWeight:500 }}>
                {monthLoading ? 'Loading…' : `${totalMonth} event${totalMonth !== 1 ? 's' : ''} in ${fmtMonthYear(calYear, calMonth)}`}
              </div>
            </div>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <button className="ev-today-btn" onClick={() => {
              const n = new Date();
              setCalYear(n.getFullYear()); setCalMonth(n.getMonth());
              setSelectedDate(toYMD(n));
            }}>
              <IcoHome2 size={13} color="#1e3a8a" />Today
            </button>
            <button className="ev-nav" onClick={prevMonth}><IcoChevL size={14} color="#1e3a8a" /></button>
            <span style={{ fontSize:14, fontWeight:700, color:'#1e3a8a', minWidth:140, textAlign:'center' }}>
              {fmtMonthYear(calYear, calMonth)}
            </span>
            <button className="ev-nav" onClick={nextMonth}><IcoChevR size={14} color="#1e3a8a" /></button>
            <button className="ev-add-btn" onClick={() => { setEditingId(null); setAddEditOpen(true); }}>
              <IcoPlus size={15} color="white" />Add Event
            </button>
          </div>
        </div>

        <div style={{
          background:'white', borderRadius:20,
          boxShadow:'0 2px 16px rgba(30,58,138,0.07)', border:'1px solid #e5e7eb',
          overflow:'hidden',
          marginRight: panelOpen ? 396 : 0,
          transition:'margin-right 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}>
          {monthLoading ? (
            <div style={{ padding:24 }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, marginBottom:12 }}>
                {Array.from({length:7}).map((_,i)=>(
                  <div key={i} className="ev-shimmer" style={{ height:14 }} />
                ))}
              </div>
              {Array.from({length:5}).map((_,ri)=>(
                <div key={ri} style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, marginBottom:3 }}>
                  {Array.from({length:7}).map((_,ci)=>(
                    <div key={ci} className="ev-shimmer" style={{ height:100 }} />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <CalendarGrid
              year={calYear} month={calMonth}
              selectedDate={selectedDate ?? ''}
              monthEvents={monthEvents}
              onSelectDate={handleSelectDate}
              onPillClick={handlePillClick}
            />
          )}
        </div>
      </div>

      {panelOpen && selectedDate && (
        <DayPanel
          date={selectedDate}
          events={dayEvents}
          loading={dayLoading}
          onClose={() => setSelectedDate(null)}
          onAdd={() => { setEditingId(null); setAddEditOpen(true); }}
          onView={id => { setSelectedId(id); setViewOpen(true); }}
          onEdit={id => { setEditingId(id); setAddEditOpen(true); }}
          onDelete={ev => { setDeletingEv(ev); setDeleteOpen(true); }}
        />
      )}

      <ViewEventModal
        open={viewOpen}
        onClose={() => { setViewOpen(false); setSelectedId(null); }}
        eventId={selectedId}
      />
      <AddEventForm
        open={addEditOpen}
        onClose={() => { setAddEditOpen(false); setEditingId(null); }}
        eventId={editingId}
        onSuccess={() => {
          refreshBoth();
          toast.current?.show({
            severity:'success', summary:'Success',
            detail: editingId ? 'Event updated!' : 'Event added!', life:4000,
          });
        }}
      />
      <GlobalDeleteModal
        open={deleteOpen}
        onClose={() => { setDeleteOpen(false); setDeletingEv(null); }}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        title="Delete Event"
        subtitle="This will permanently delete the event."
      >
        {deletingEv && (
          <div style={{
            padding:'14px 16px', borderRadius:10,
            background:'rgba(239,68,68,0.05)', border:'1px solid rgba(239,68,68,0.15)',
          }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#1f2937', marginBottom:4 }}>{deletingEv.title}</div>
            <div style={{ fontSize:12, color:'#6b7280' }}>{deletingEv.body}</div>
          </div>
        )}
      </GlobalDeleteModal>
    </>
  );
};

export default Events;


// import React, { useState, useEffect, useRef, useCallback } from 'react';
// import { Toast } from 'primereact/toast';
// import apiClient from '../../stateManagers/services/axiosInterceptor';
// import GlobalDeleteModal from '../../components/GlobalDeleteModal';
// import AddEventForm from './parts/AddEventForm';
// import ViewEventModal from './parts/ViewEventModal';

// // ─────────────────────────────────────────────────────────────────────────────
// // Types
// // ─────────────────────────────────────────────────────────────────────────────
// interface EventData {
//   id: number;
//   title: string;
//   body: string;
//   event_date: string;
//   event_time: string | null;
//   event_incharge: string | null;
//   posted_by: string;
//   created_at: string;
//   updated_at: string;
// }

// // ─────────────────────────────────────────────────────────────────────────────
// // Inline SVG — zero PrimeIcons dependency
// // ─────────────────────────────────────────────────────────────────────────────
// const Ico = ({ d, size = 16, color = 'currentColor', style = {} }: {
//   d: string | string[]; size?: number; color?: string; style?: React.CSSProperties;
// }) => (
//   <svg width={size} height={size} viewBox="0 0 24 24"
//     fill="none" stroke={color} strokeWidth={1.8}
//     strokeLinecap="round" strokeLinejoin="round"
//     style={{ flexShrink: 0, display: 'block', ...style }}>
//     {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
//   </svg>
// );

// const IcoCalendar = (p: { size?: number; color?: string }) => <Ico {...p} d={['M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z']} />;
// const IcoPlus     = (p: { size?: number; color?: string }) => <Ico {...p} d={['M12 5v14', 'M5 12h14']} />;
// const IcoChevL    = (p: { size?: number; color?: string }) => <Ico {...p} d="M15 18l-6-6 6-6" />;
// const IcoChevR    = (p: { size?: number; color?: string }) => <Ico {...p} d="M9 18l6-6-6-6" />;
// const IcoEye      = (p: { size?: number; color?: string }) => <Ico {...p} d={['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z','M12 9a3 3 0 100 6 3 3 0 000-6z']} />;
// const IcoPencil   = (p: { size?: number; color?: string }) => <Ico {...p} d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />;
// const IcoTrash    = (p: { size?: number; color?: string }) => <Ico {...p} d={['M3 6h18','M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6','M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2','M10 11v6','M14 11v6']} />;
// const IcoClock    = (p: { size?: number; color?: string }) => <Ico {...p} d={['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z','M12 6v6l4 2']} />;
// const IcoX        = (p: { size?: number; color?: string }) => <Ico {...p} d={['M18 6L6 18','M6 6l12 12']} />;
// const IcoHome2    = (p: { size?: number; color?: string }) => <Ico {...p} d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" />;
// const IcoSpinner  = (p: { size?: number; color?: string }) => <Ico {...p}
//   d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"
//   style={{ animation: 'ev-spin 1s linear infinite' }} />;

// // ─────────────────────────────────────────────────────────────────────────────
// // Helpers
// // ─────────────────────────────────────────────────────────────────────────────
// const toYMD = (d: Date) =>
//   `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

// const fmtMonthYear = (y: number, m: number) =>
//   new Date(y, m, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

// const fmtTime = (t: string) =>
//   new Date(`1970-01-01T${t}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

// const fmtFullDate = (ymd: string) =>
//   new Date(ymd + 'T00:00:00').toLocaleDateString('en-US', {
//     weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
//   });

// // ─────────────────────────────────────────────────────────────────────────────
// // CSS
// // ─────────────────────────────────────────────────────────────────────────────
// const CSS = `
//   @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
//   .ev-root * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }

//   @keyframes ev-fadeIn   { from{opacity:0}          to{opacity:1} }
//   @keyframes ev-slideUp  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
//   @keyframes ev-slideIn  { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
//   @keyframes ev-shimmer  { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
//   @keyframes ev-spin     { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }

//   /* ── Calendar grid cell ── */
//   .ev-cell {
//     min-height: 72px;
//     border-right: 1px solid #f0f4f8;
//     border-bottom: 1px solid #f0f4f8;
//     padding: 4px 5px 5px;
//     cursor: pointer;
//     transition: background 0.13s;
//     position: relative;
//     vertical-align: top;
//   }
//   .ev-cell:hover { background: rgba(59,130,246,0.04); }
//   .ev-cell--other { background: #fafbfc; }
//   .ev-cell--other .ev-day-num { color: #c9d2dc; }
//   .ev-cell--today { background: rgba(30,58,138,0.03); }
//   .ev-cell--selected { background: rgba(59,130,246,0.07) !important; }

//   .ev-day-num {
//     width: 22px; height: 22px; border-radius: 50%;
//     display: flex; align-items: center; justify-content: center;
//     font-size: 13px; font-weight: 600; color: #4b5563;
//     margin-bottom: 3px; flex-shrink: 0;
//     transition: background 0.13s, color 0.13s;
//   }
//   .ev-day-num--today {
//     background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
//     color: white !important;
//     box-shadow: 0 2px 8px rgba(59,130,246,0.35);
//   }
//   .ev-day-num--selected:not(.ev-day-num--today) {
//     background: rgba(59,130,246,0.15);
//     color: #1e3a8a !important;
//   }

//   /* ── Event pill inside calendar cell ── */
//   .ev-pill {
//     display: flex; align-items: center; gap: 4px;
//     padding: 1px 5px; border-radius: 4px; margin-bottom: 2px;
//     font-size: 10px; font-weight: 600; cursor: pointer;
//     background: linear-gradient(90deg, #1e3a8a 0%, #2855ae 100%);
//     color: white; white-space: nowrap; overflow: hidden;
//     text-overflow: ellipsis; max-width: 100%;
//     transition: opacity 0.13s, transform 0.13s;
//     animation: ev-slideUp 0.18s ease both;
//   }
//   .ev-pill:hover { opacity: 0.85; transform: scale(1.02); }
//   .ev-pill--more {
//     background: rgba(30,58,138,0.1); color: #1e3a8a;
//     font-size: 10px; font-weight: 700;
//   }

//   /* ── Day panel (right slide-in) ── */
//   .ev-panel {
//     position: fixed; top: 0; right: 0; bottom: 0;
//     width: 380px; background: white;
//     box-shadow: -4px 0 40px rgba(30,58,138,0.14);
//     display: flex; flex-direction: column;
//     z-index: 1000;
//     animation: ev-slideIn 0.25s cubic-bezier(0.4,0,0.2,1);
//   }

//   /* ── Event card in panel ── */
//   .ev-event-card {
//     border-radius: 12px; border: 1px solid rgba(30,58,138,0.1);
//     overflow: hidden; background: white;
//     transition: box-shadow 0.15s, transform 0.13s;
//     animation: ev-slideUp 0.2s ease both;
//   }
//   .ev-event-card:hover {
//     box-shadow: 0 4px 20px rgba(30,58,138,0.12);
//     transform: translateY(-1px);
//   }

//   /* ── Action buttons ── */
//   .ev-act {
//     width: 28px; height: 28px; border-radius: 7px;
//     display: flex; align-items: center; justify-content: center;
//     border: 1px solid transparent; cursor: pointer;
//     background: transparent; padding: 0;
//     transition: background 0.13s;
//   }
//   .ev-act:hover      { background: rgba(59,130,246,0.1); border-color: rgba(59,130,246,0.2); }
//   .ev-act--red:hover { background: rgba(239,68,68,0.1);  border-color: rgba(239,68,68,0.2); }

//   /* ── Nav buttons ── */
//   .ev-nav {
//     width: 34px; height: 34px; border-radius: 10px;
//     background: rgba(30,58,138,0.07); border: 1px solid rgba(30,58,138,0.13);
//     cursor: pointer; display: flex; align-items: center; justify-content: center;
//     transition: background 0.13s; padding: 0;
//   }
//   .ev-nav:hover { background: rgba(30,58,138,0.14); }

//   /* ── Shimmer ── */
//   .ev-shimmer {
//     background: linear-gradient(90deg,#f0f4f8 25%,#e2e8f0 50%,#f0f4f8 75%);
//     background-size: 600px 100%;
//     animation: ev-shimmer 1.3s infinite linear;
//     border-radius: 6px;
//   }

//   /* ── Scroll ── */
//   .ev-scroll::-webkit-scrollbar { width: 4px; }
//   .ev-scroll::-webkit-scrollbar-track { background: transparent; }
//   .ev-scroll::-webkit-scrollbar-thumb { background: rgba(30,58,138,0.15); border-radius: 4px; }

//   /* ── Add button ── */
//   .ev-add-btn {
//     display: flex; align-items: center; gap: 8px;
//     padding: 10px 20px; border-radius: 12px; border: none;
//     background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
//     color: white; cursor: pointer; font-size: 14px; font-weight: 600;
//     box-shadow: 0 4px 12px rgba(30,58,138,0.3);
//     font-family: 'DM Sans', sans-serif;
//     transition: box-shadow 0.18s, transform 0.14s;
//   }
//   .ev-add-btn:hover {
//     box-shadow: 0 6px 20px rgba(30,58,138,0.4);
//     transform: translateY(-1px);
//   }

//   /* ── Today btn ── */
//   .ev-today-btn {
//     display: flex; align-items: center; gap: 7px;
//     padding: 7px 14px; border-radius: 9px;
//     background: rgba(30,58,138,0.07); border: 1px solid rgba(30,58,138,0.15);
//     color: #1e3a8a; font-weight: 600; font-size: 13px; cursor: pointer;
//     font-family: 'DM Sans', sans-serif; transition: background 0.13s;
//   }
//   .ev-today-btn:hover { background: rgba(30,58,138,0.13); }
// `;

// // ─────────────────────────────────────────────────────────────────────────────
// // EventCard (inside day panel)
// // ─────────────────────────────────────────────────────────────────────────────
// const EventCard: React.FC<{
//   event: EventData; index: number;
//   onView: () => void; onEdit: () => void; onDelete: () => void;
// }> = ({ event, index, onView, onEdit, onDelete }) => (
//   <div className="ev-event-card" style={{ animationDelay: `${index * 0.05}s` }}>
//     <div style={{ height: 3, background: 'linear-gradient(90deg,#1e3a8a,#3b82f6)' }} />
//     <div style={{ padding: '11px 13px' }}>

//       <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:6, marginBottom:7 }}>
//         {event.event_time ? (
//           <span style={{
//             display:'flex', alignItems:'center', gap:4,
//             fontSize:11, fontWeight:700, color:'#3b82f6',
//             background:'rgba(59,130,246,0.09)', border:'1px solid rgba(59,130,246,0.18)',
//             padding:'2px 7px', borderRadius:5, fontFamily:'monospace',
//           }}>
//             <IcoClock size={10} color="#3b82f6" />{fmtTime(event.event_time)}
//           </span>
//         ) : (
//           <span style={{
//             fontSize:11, fontWeight:600, color:'#9ca3af',
//             background:'#f9fafb', border:'1px solid #e5e7eb',
//             padding:'2px 7px', borderRadius:5,
//           }}>All day</span>
//         )}
//         <div style={{ display:'flex', gap:3 }} onClick={e => e.stopPropagation()}>
//           <button className="ev-act"         title="View"   onClick={onView}  ><IcoEye    size={13} color="#60a5fa" /></button>
//           <button className="ev-act"         title="Edit"   onClick={onEdit}  ><IcoPencil size={13} color="#3b82f6" /></button>
//           <button className="ev-act ev-act--red" title="Delete" onClick={onDelete}><IcoTrash  size={13} color="#ef4444" /></button>
//         </div>
//       </div>

//       <div style={{ fontSize:13, fontWeight:700, color:'#111827', marginBottom:4, lineHeight:1.3 }}>
//         {event.title}
//       </div>
//       <div style={{
//         fontSize:11, color:'#6b7280', lineHeight:1.5, marginBottom:8,
//         display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden',
//       }}>
//         {event.body}
//       </div>

//       <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:5 }}>
//         <div style={{ display:'flex', alignItems:'center', gap:5 }}>
//           <div style={{
//             width:20, height:20, borderRadius:'50%',
//             background:'linear-gradient(135deg,#1e3a8a,#3b82f6)',
//             display:'flex', alignItems:'center', justifyContent:'center',
//             fontSize:8, fontWeight:800, color:'white',
//           }}>{event.posted_by.charAt(0).toUpperCase()}</div>
//           <span style={{ fontSize:11, color:'#9ca3af' }}>{event.posted_by}</span>
//         </div>
//         {event.event_incharge && (
//           <div style={{ display:'flex', alignItems:'center', gap:4 }}>
//             <div style={{
//               width:18, height:18, borderRadius:'50%',
//               background:'linear-gradient(135deg,#0f766e,#14b8a6)',
//               display:'flex', alignItems:'center', justifyContent:'center',
//               fontSize:7, fontWeight:800, color:'white',
//             }}>{event.event_incharge.charAt(0).toUpperCase()}</div>
//             <span style={{ fontSize:11, color:'#6b7280' }}>{event.event_incharge}</span>
//           </div>
//         )}
//       </div>
//     </div>
//   </div>
// );

// // ─────────────────────────────────────────────────────────────────────────────
// // Day Panel (slide-in from right)
// // ─────────────────────────────────────────────────────────────────────────────
// const DayPanel: React.FC<{
//   date: string;
//   events: EventData[];
//   loading: boolean;
//   onClose: () => void;
//   onAdd: () => void;
//   onView: (id: number) => void;
//   onEdit: (id: number) => void;
//   onDelete: (ev: EventData) => void;
// }> = ({ date, events, loading, onClose, onAdd, onView, onEdit, onDelete }) => (
//   <div className="ev-panel">
//     {/* Panel header */}
//     <div style={{
//       padding:'18px 20px', flexShrink:0,
//       background:'linear-gradient(135deg,#1e3a8a 0%,#2855ae 55%,#3b82f6 100%)',
//     }}>
//       <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
//         <div>
//           <div style={{ fontSize:10, fontWeight:700, color:'rgba(255,255,255,0.55)', textTransform:'uppercase', letterSpacing:'0.7px', marginBottom:3 }}>
//             Selected Day
//           </div>
//           <div style={{ fontSize:15, fontWeight:800, color:'white', lineHeight:1.2 }}>
//             {fmtFullDate(date)}
//           </div>
//         </div>
//         <button onClick={onClose} style={{
//           width:32, height:32, borderRadius:9, background:'rgba(255,255,255,0.15)',
//           border:'1px solid rgba(255,255,255,0.25)', cursor:'pointer',
//           display:'flex', alignItems:'center', justifyContent:'center', padding:0,
//         }}>
//           <IcoX size={15} color="white" />
//         </button>
//       </div>

//       <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
//         <div style={{
//           minWidth:36, height:32, borderRadius:9, padding:'0 12px',
//           background:'rgba(255,255,255,0.18)', border:'1px solid rgba(255,255,255,0.28)',
//           display:'flex', alignItems:'center', gap:6,
//           fontSize:13, fontWeight:800, color:'white',
//         }}>
//           {loading ? <IcoSpinner size={13} color="rgba(255,255,255,0.8)" /> : (
//             <><span style={{ fontSize:16 }}>{events.length}</span>
//             <span style={{ fontSize:10, fontWeight:600, color:'rgba(255,255,255,0.65)' }}>
//               event{events.length !== 1 ? 's' : ''}
//             </span></>
//           )}
//         </div>
//         <button onClick={onAdd} style={{
//           display:'flex', alignItems:'center', gap:6, padding:'7px 14px',
//           border:'1px solid rgba(255,255,255,0.28)', borderRadius:9,
//           background:'rgba(255,255,255,0.15)', color:'white', cursor:'pointer',
//           fontSize:12, fontWeight:700, fontFamily:'DM Sans,sans-serif',
//         }}>
//           <IcoPlus size={12} color="white" />Add Event
//         </button>
//       </div>
//     </div>

//     {/* Panel body */}
//     <div className="ev-scroll" style={{ flex:1, overflowY:'auto', padding:16, display:'flex', flexDirection:'column', gap:10 }}>
//       {loading ? (
//         Array.from({length:2}).map((_,i) => (
//           <div key={i} style={{ borderRadius:12, overflow:'hidden', border:'1px solid #f0f4f8' }}>
//             <div className="ev-shimmer" style={{ height:3 }} />
//             <div style={{ padding:12, display:'flex', flexDirection:'column', gap:8 }}>
//               <div className="ev-shimmer" style={{ height:11, width:'25%' }} />
//               <div className="ev-shimmer" style={{ height:14, width:'65%' }} />
//               <div className="ev-shimmer" style={{ height:10, width:'85%' }} />
//             </div>
//           </div>
//         ))
//       ) : events.length === 0 ? (
//         <div style={{
//           display:'flex', flexDirection:'column', alignItems:'center',
//           justifyContent:'center', padding:'40px 20px', gap:12,
//           animation:'ev-fadeIn 0.3s ease',
//         }}>
//           <div style={{
//             width:54, height:54, borderRadius:14,
//             background:'rgba(30,58,138,0.06)', border:'1.5px dashed rgba(59,130,246,0.25)',
//             display:'flex', alignItems:'center', justifyContent:'center',
//           }}>
//             <IcoCalendar size={24} color="#93c5fd" />
//           </div>
//           <div style={{ fontSize:14, fontWeight:700, color:'#374151' }}>No events</div>
//           <div style={{ fontSize:12, color:'#9ca3af', textAlign:'center', lineHeight:1.6 }}>
//             Nothing scheduled.<br />Click <strong>Add Event</strong> to create one.
//           </div>
//         </div>
//       ) : events.map((ev, i) => (
//         <EventCard
//           key={ev.id} event={ev} index={i}
//           onView={() => onView(ev.id)}
//           onEdit={() => onEdit(ev.id)}
//           onDelete={() => onDelete(ev)}
//         />
//       ))}
//     </div>
//   </div>
// );

// // ─────────────────────────────────────────────────────────────────────────────
// // Full-month Calendar Grid
// // ─────────────────────────────────────────────────────────────────────────────
// const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// const CalendarGrid: React.FC<{
//   year: number; month: number;
//   selectedDate: string;
//   monthEvents: Record<string, EventData[]>;
//   onSelectDate: (ymd: string) => void;
//   onPillClick: (ev: EventData) => void;
// }> = ({ year, month, selectedDate, monthEvents, onSelectDate, onPillClick }) => {
//   const today    = toYMD(new Date());
//   const firstDay = new Date(year, month, 1).getDay();
//   const daysInMo = new Date(year, month + 1, 0).getDate();
//   const daysInPr = new Date(year, month, 0).getDate();

//   // Build 6-row × 7-col grid
//   const cells: { ymd: string; day: number; cur: boolean }[] = [];
//   for (let i = firstDay - 1; i >= 0; i--) {
//     const d = daysInPr - i;
//     cells.push({ ymd:`${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`, day:d, cur:false });
//   }
//   for (let d = 1; d <= daysInMo; d++) {
//     cells.push({ ymd:`${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`, day:d, cur:true });
//   }
//   while (cells.length < 42) {
//     const d  = cells.length - firstDay - daysInMo + 1;
//     const nm = month + 2 > 12 ? 1      : month + 2;
//     const ny = month + 2 > 12 ? year+1 : year;
//     cells.push({ ymd:`${ny}-${String(nm).padStart(2,'0')}-${String(d).padStart(2,'0')}`, day:d, cur:false });
//   }

//   const MAX_PILLS = 3; // max event pills shown per cell before "+N more"

//   return (
//     <div style={{ width:'100%' }}>
//       {/* Week-day headers */}
//       <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', borderBottom:'2px solid #e5e7eb' }}>
//         {WEEK_DAYS.map((d, i) => (
//           <div key={d} style={{
//             padding:'7px 0', textAlign:'center',
//             fontSize:11, fontWeight:800, color: i === 0 || i === 6 ? '#9ca3af' : '#6b7280',
//             textTransform:'uppercase', letterSpacing:'0.5px',
//             borderRight: i < 6 ? '1px solid #f0f4f8' : 'none',
//           }}>
//             {d}
//           </div>
//         ))}
//       </div>

//       {/* Day cells */}
//       <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)' }}>
//         {cells.map((cell, i) => {
//           const isToday  = cell.ymd === today;
//           const isSel    = cell.ymd === selectedDate;
//           const evs      = monthEvents[cell.ymd] ?? [];
//           const shown    = evs.slice(0, MAX_PILLS);
//           const overflow = evs.length - MAX_PILLS;

//           return (
//             <div
//               key={i}
//               className={[
//                 'ev-cell',
//                 !cell.cur ? 'ev-cell--other'    : '',
//                 isToday   ? 'ev-cell--today'    : '',
//                 isSel     ? 'ev-cell--selected' : '',
//               ].join(' ')}
//               onClick={() => onSelectDate(cell.ymd)}
//               style={{ borderLeft: i % 7 === 0 ? 'none' : undefined }}
//             >
//               {/* Day number */}
//               <div className={[
//                 'ev-day-num',
//                 isToday ? 'ev-day-num--today'    : '',
//                 isSel   ? 'ev-day-num--selected' : '',
//               ].join(' ')}>
//                 {cell.day}
//               </div>

//               {/* Event pills */}
//               {shown.map((ev, pi) => (
//                 <div
//                   key={ev.id}
//                   className="ev-pill"
//                   style={{ animationDelay:`${pi * 0.04}s` }}
//                   onClick={e => { e.stopPropagation(); onPillClick(ev); }}
//                   title={ev.title}
//                 >
//                   {ev.event_time && (
//                     <span style={{ opacity:0.75, fontSize:9, fontFamily:'monospace', flexShrink:0 }}>
//                       {fmtTime(ev.event_time)}
//                     </span>
//                   )}
//                   <span style={{ overflow:'hidden', textOverflow:'ellipsis' }}>{ev.title}</span>
//                 </div>
//               ))}

//               {overflow > 0 && (
//                 <div className="ev-pill ev-pill--more">
//                   +{overflow} more
//                 </div>
//               )}
//             </div>
//           );
//         })}
//       </div>
//     </div>
//   );
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // Main component
// // ─────────────────────────────────────────────────────────────────────────────
// const Events: React.FC = () => {
//   const today = new Date();
//   const [calYear,      setCalYear]      = useState(today.getFullYear());
//   const [calMonth,     setCalMonth]     = useState(today.getMonth());
//   const [selectedDate, setSelectedDate] = useState<string | null>(null);  // null = panel closed

//   // month events keyed by ymd
//   const [monthEvents,  setMonthEvents]  = useState<Record<string, EventData[]>>({});
//   const [monthLoading, setMonthLoading] = useState(false);

//   // day events (for panel)
//   const [dayEvents,    setDayEvents]    = useState<EventData[]>([]);
//   const [dayLoading,   setDayLoading]   = useState(false);

//   // modals
//   const [viewOpen,      setViewOpen]      = useState(false);
//   const [selectedId,    setSelectedId]    = useState<number | null>(null);
//   const [addEditOpen,   setAddEditOpen]   = useState(false);
//   const [editingId,     setEditingId]     = useState<number | null>(null);
//   const [deleteOpen,    setDeleteOpen]    = useState(false);
//   const [deletingEv,    setDeletingEv]    = useState<EventData | null>(null);
//   const [deleteLoading, setDeleteLoading] = useState(false);

//   const toast = useRef<Toast>(null);

//   // ── fetch full month ──────────────────────────────────────────────────────
//   const fetchMonth = useCallback(async (y: number, m: number) => {
//     setMonthLoading(true);
//     const ym = `${y}-${String(m+1).padStart(2,'0')}`;
//     try {
//       const res = await apiClient.get(`/events-by-month?month=${ym}`);
//       if (res.data.status === 'success') {
//         const grouped: Record<string, EventData[]> = {};
//         (res.data.events as EventData[]).forEach(ev => {
//           const k = ev.event_date.substring(0, 10);
//           if (!grouped[k]) grouped[k] = [];
//           grouped[k].push(ev);
//         });
//         // Sort each day's events by time
//         Object.values(grouped).forEach(arr =>
//           arr.sort((a, b) => (a.event_time ?? '').localeCompare(b.event_time ?? ''))
//         );
//         setMonthEvents(grouped);
//       }
//     } catch { /* silent */ }
//     finally { setMonthLoading(false); }
//   }, []);

//   // ── fetch day events (for panel) ──────────────────────────────────────────
//   const fetchDay = useCallback(async (ymd: string) => {
//     setDayLoading(true);
//     setDayEvents([]);
//     try {
//       const res = await apiClient.get(`/events-by-date?event_date=${ymd}&limit=100`);
//       if (res.data.status === 'success') setDayEvents(res.data.events ?? []);
//     } catch {
//       toast.current?.show({ severity:'error', summary:'Error', detail:'Failed to load events', life:4000 });
//     } finally { setDayLoading(false); }
//   }, []);

//   useEffect(() => { fetchMonth(calYear, calMonth); }, [calYear, calMonth]);

//   useEffect(() => {
//     if (selectedDate) fetchDay(selectedDate);
//   }, [selectedDate]);

//   const refreshBoth = () => {
//     fetchMonth(calYear, calMonth);
//     if (selectedDate) fetchDay(selectedDate);
//   };

//   const prevMonth = () => {
//     if (calMonth === 0) { setCalYear(y => y-1); setCalMonth(11); }
//     else setCalMonth(m => m-1);
//   };
//   const nextMonth = () => {
//     if (calMonth === 11) { setCalYear(y => y+1); setCalMonth(0); }
//     else setCalMonth(m => m+1);
//   };

//   const handleSelectDate = (ymd: string) => {
//     setSelectedDate(prev => prev === ymd ? null : ymd); // toggle
//   };

//   const handlePillClick = (ev: EventData) => {
//     setSelectedDate(ev.event_date.substring(0, 10));
//   };

//   const handleDeleteConfirm = async () => {
//     if (!deletingEv) return;
//     setDeleteLoading(true);
//     try {
//       await apiClient.delete(`/events/${deletingEv.id}`);
//       toast.current?.show({ severity:'success', summary:'Deleted', detail:'Event removed.', life:4000 });
//       setDeleteOpen(false); setDeletingEv(null);
//       refreshBoth();
//     } catch (err: any) {
//       toast.current?.show({ severity:'error', summary:'Error', detail:err.response?.data?.message || 'Delete failed', life:5000 });
//     } finally { setDeleteLoading(false); }
//   };

//   const totalMonth = Object.values(monthEvents).reduce((a, b) => a + b.length, 0);
//   const panelOpen  = selectedDate !== null;

//   return (
//     <>
//       <Toast ref={toast} position="top-right" />
//       <style>{CSS}</style>

//       <div className="ev-root" style={{ minHeight:'100vh' }}>

//         {/* ── Page header ── */}
//         <div style={{
//           background:'white', borderRadius:'20px', padding:'20px 24px',
//           marginBottom:'20px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)',
//           border:'1px solid #e5e7eb', display:'flex',
//           justifyContent:'space-between', alignItems:'center',
//           flexWrap:'wrap', gap:16,
//         }}>
//           <div style={{ display:'flex', alignItems:'center', gap:16 }}>
//             <div style={{
//               width:50, height:50,
//               background:'linear-gradient(135deg,#1e3a8a 0%,#3b82f6 100%)',
//               borderRadius:15, display:'flex', alignItems:'center', justifyContent:'center',
//               boxShadow:'0 4px 12px rgba(30,58,138,0.25)', flexShrink:0,
//             }}>
//               <IcoCalendar size={23} color="white" />
//             </div>
//             <div>
//               <h1 style={{ margin:0, color:'#1f2937', fontSize:22, fontWeight:700, letterSpacing:'-0.5px' }}>
//                 Events
//               </h1>
//               <div style={{ color:'#6b7280', fontSize:13, marginTop:3, fontWeight:500 }}>
//                 {monthLoading ? 'Loading…' : `${totalMonth} event${totalMonth !== 1 ? 's' : ''} in ${fmtMonthYear(calYear, calMonth)}`}
//               </div>
//             </div>
//           </div>

//           <div style={{ display:'flex', alignItems:'center', gap:10 }}>
//             {/* Month nav */}
//             <button className="ev-today-btn" onClick={() => {
//               const n = new Date();
//               setCalYear(n.getFullYear()); setCalMonth(n.getMonth());
//               setSelectedDate(toYMD(n));
//             }}>
//               <IcoHome2 size={13} color="#1e3a8a" />Today
//             </button>
//             <button className="ev-nav" onClick={prevMonth}><IcoChevL size={14} color="#1e3a8a" /></button>
//             <span style={{ fontSize:14, fontWeight:700, color:'#1e3a8a', minWidth:140, textAlign:'center' }}>
//               {fmtMonthYear(calYear, calMonth)}
//             </span>
//             <button className="ev-nav" onClick={nextMonth}><IcoChevR size={14} color="#1e3a8a" /></button>
//             {/* Add */}
//             <button className="ev-add-btn" onClick={() => { setEditingId(null); setAddEditOpen(true); }}>
//               <IcoPlus size={15} color="white" />Add Event
//             </button>
//           </div>
//         </div>

//         {/* ── Calendar card ── */}
//         <div style={{
//           background:'white', borderRadius:20,
//           boxShadow:'0 2px 16px rgba(30,58,138,0.07)', border:'1px solid #e5e7eb',
//           overflow:'hidden',
//           // shrink horizontally when panel is open
//           marginRight: panelOpen ? 396 : 0,
//           transition:'margin-right 0.25s cubic-bezier(0.4,0,0.2,1)',
//         }}>
//           {monthLoading ? (
//             /* skeleton */
//             <div style={{ padding:24 }}>
//               <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, marginBottom:12 }}>
//                 {Array.from({length:7}).map((_,i)=>(
//                   <div key={i} className="ev-shimmer" style={{ height:14 }} />
//                 ))}
//               </div>
//               {Array.from({length:5}).map((_,ri)=>(
//                 <div key={ri} style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, marginBottom:3 }}>
//                   {Array.from({length:7}).map((_,ci)=>(
//                     <div key={ci} className="ev-shimmer" style={{ height:100 }} />
//                   ))}
//                 </div>
//               ))}
//             </div>
//           ) : (
//             <CalendarGrid
//               year={calYear} month={calMonth}
//               selectedDate={selectedDate ?? ''}
//               monthEvents={monthEvents}
//               onSelectDate={handleSelectDate}
//               onPillClick={handlePillClick}
//             />
//           )}
//         </div>

//       </div>

//       {/* ── Day panel (slide in from right) ── */}
//       {panelOpen && selectedDate && (
//         <>
//           {/* Backdrop (transparent — calendar just shrinks) */}
//           <DayPanel
//             date={selectedDate}
//             events={dayEvents}
//             loading={dayLoading}
//             onClose={() => setSelectedDate(null)}
//             onAdd={() => { setEditingId(null); setAddEditOpen(true); }}
//             onView={id => { setSelectedId(id); setViewOpen(true); }}
//             onEdit={id => { setEditingId(id); setAddEditOpen(true); }}
//             onDelete={ev => { setDeletingEv(ev); setDeleteOpen(true); }}
//           />
//         </>
//       )}

//       {/* ── Modals ── */}
//       <ViewEventModal
//         open={viewOpen}
//         onClose={() => { setViewOpen(false); setSelectedId(null); }}
//         eventId={selectedId}
//       />
//       <AddEventForm
//         open={addEditOpen}
//         onClose={() => { setAddEditOpen(false); setEditingId(null); }}
//         eventId={editingId}
//         onSuccess={() => {
//           refreshBoth();
//           toast.current?.show({
//             severity:'success', summary:'Success',
//             detail: editingId ? 'Event updated!' : 'Event added!', life:4000,
//           });
//         }}
//       />
//       <GlobalDeleteModal
//         open={deleteOpen}
//         onClose={() => { setDeleteOpen(false); setDeletingEv(null); }}
//         onConfirm={handleDeleteConfirm}
//         loading={deleteLoading}
//         title="Delete Event"
//         subtitle="This will permanently delete the event."
//       >
//         {deletingEv && (
//           <div style={{
//             padding:'14px 16px', borderRadius:10,
//             background:'rgba(239,68,68,0.05)', border:'1px solid rgba(239,68,68,0.15)',
//           }}>
//             <div style={{ fontSize:13, fontWeight:700, color:'#1f2937', marginBottom:4 }}>{deletingEv.title}</div>
//             <div style={{ fontSize:12, color:'#6b7280' }}>{deletingEv.body}</div>
//           </div>
//         )}
//       </GlobalDeleteModal>
//     </>
//   );
// };

// export default Events;